import { useEffect, useCallback } from 'react';
import api from '../api';

// Simple polling-based notifications
class NotificationService {
  constructor() {
    this.listeners = new Map();
    this.polling = false;
    this.pollInterval = null;
    this.lastCheck = Date.now();
  }

  subscribe(channel, callback) {
    if (!this.listeners.has(channel)) {
      this.listeners.set(channel, new Set());
    }
    this.listeners.get(channel).add(callback);

    // Start polling if not already started
    if (!this.polling) {
      this.startPolling();
    }
  }

  unsubscribe(channel, callback) {
    if (this.listeners.has(channel)) {
      this.listeners.get(channel).delete(callback);

      // Stop polling if no listeners
      if (this.listeners.get(channel).size === 0) {
        this.listeners.delete(channel);
      }
    }

    if (this.listeners.size === 0) {
      this.stopPolling();
    }
  }

  emit(channel, data) {
    if (this.listeners.has(channel)) {
      this.listeners.get(channel).forEach(callback => {
        callback(data);
      });
    }
  }

  async fetchNotifications() {
    try {
      const response = await api.get('/candidate/notifications');
      if (response.data.notifications && response.data.notifications.length > 0) {
        response.data.notifications.forEach(notification => {
          this.emit('notifications', notification);
        });
      }
    } catch (error) {
      // Silent fail - don't spam console in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching notifications:', error);
      }
    }
  }

  startPolling() {
    this.polling = true;
    // Poll every 30 seconds for updates
    this.pollInterval = setInterval(() => {
      this.fetchNotifications();
    }, 30000);

    // Fetch immediately on start
    this.fetchNotifications();
  }

  stopPolling() {
    this.polling = false;
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  // Simulated notification (for demo purposes)
  simulateNotification(candidateId, message, type = 'info') {
    this.emit(`candidate.${candidateId}`, {
      message,
      type,
      timestamp: new Date().toISOString(),
    });
  }
}

// Singleton instance
const notificationService = new NotificationService();

// React hook for using notifications
export const useNotifications = (candidateId) => {
  const channel = `candidate.${candidateId}`;

  const handleNotification = useCallback((notification) => {
    // Show notification using SweetAlert or browser notification
    console.log('Notification received:', notification);

    // You can integrate with sweetalert2 here
    if (window.Swal) {
      window.Swal.fire({
        toast: true,
        position: 'top-end',
        icon: notification.type,
        title: notification.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  }, []);

  useEffect(() => {
    if (candidateId) {
      notificationService.subscribe(channel, handleNotification);

      return () => {
        notificationService.unsubscribe(channel, handleNotification);
      };
    }
  }, [candidateId, channel, handleNotification]);

  return {
    notify: (message, type) => {
      notificationService.simulateNotification(candidateId, message, type);
    }
  };
};

export default notificationService;
